import { Component, HostListener, computed, effect, inject, input, linkedSignal, output, signal, untracked } from '@angular/core';

import { Photo } from '../order.model';
import { PhotoService } from '../photo.service';

/**
 * Full-screen viewer for browsing photos at screen size (the `web` copy).
 * The parent shows it with `@if` and removes it on `close`.
 */
@Component({
  selector: 'app-photo-viewer',
  standalone: true,
  imports: [],
  templateUrl: './photo-viewer.html'
})
export class PhotoViewer {
  photos = input.required<Photo[]>();
  startIndex = input(0);
  close = output<void>();

  index = linkedSignal(() => this.startIndex());
  current = computed<Photo | undefined>(() => this.photos()[this.index()]);
  nextPhoto = computed<Photo | undefined>(() => this.photos()[this.index() + 1]);
  hasPrevious = computed(() => this.index() > 0);
  hasNext = computed(() => this.index() < this.photos().length - 1);

  url = signal<string | null>(null);
  // True until the image itself has loaded, not just its download URL.
  loading = signal(false);
  failed = signal(false);

  private photoService = inject(PhotoService);
  // Promises are cached so a prefetch and a later load of the same photo share one request.
  private urlCache = new Map<string, Promise<string>>();
  // Ignores results for photos the person has already moved past.
  private requestId = 0;

  constructor() {
    // Reads photos only through computeds, so a new array holding the same photos
    // (parents rebuild it on every change detection) doesn't restart the load.
    effect(() => {
      const photo = this.current();
      const nextPhoto = this.nextPhoto();

      untracked(() => {
        this.show(photo);
        if (nextPhoto) {
          this.prefetch(nextPhoto);
        }
      });
    });
  }

  previous() {
    if (this.hasPrevious()) {
      this.index.update(index => index - 1);
    }
  }

  next() {
    if (this.hasNext()) {
      this.index.update(index => index + 1);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowLeft':
        this.previous();
        break;
      case 'ArrowRight':
        this.next();
        break;
      case 'Escape':
        this.close.emit();
        break;
    }
  }

  onImageLoad() {
    this.loading.set(false);
  }

  onImageError() {
    console.error('Failed to load web photo:', this.current()?.blobName);
    this.loading.set(false);
    this.failed.set(true);
  }

  private async show(photo: Photo | undefined) {
    const requestId = ++this.requestId;
    this.url.set(null);
    this.failed.set(false);

    if (!photo) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    try {
      const url = await this.urlFor(photo);
      if (requestId === this.requestId) {
        // `loading` turns off when the <img> fires `load`.
        this.url.set(url);
      }
    } catch (error) {
      if (requestId === this.requestId) {
        console.error('Failed to load web photo:', photo.blobName, error);
        this.loading.set(false);
        this.failed.set(true);
      }
    }
  }

  // Resolves the URL and starts downloading the image, so moving forward shows it right away.
  private prefetch(photo: Photo) {
    this.urlFor(photo)
      .then(url => {
        new Image().src = url;
      })
      .catch(() => {
        // Reported when the person actually opens this photo.
      });
  }

  private urlFor(photo: Photo): Promise<string> {
    const blobName = photo.blobName;
    let url = this.urlCache.get(blobName);
    if (!url) {
      url = this.photoService.getWebUrl(photo);
      this.urlCache.set(blobName, url);
      // Don't keep a failure, so coming back to the photo tries again.
      url.catch(() => this.urlCache.delete(blobName));
    }
    return url;
  }
}
